/**
 * 通用能力（Alice 页面 capabilities 的复刻）：按次或按量计费的非对话能力（生图、视频、语音、向量、转写、音乐等）的调用、收入与单价。
 *
 * 背景：Alice 把网络搜索、网页读取、抠图等「能力」单独统计；本仓的对应物是非 chat 类型的已发布模型。
 *   一行一个模型，统计区间内的调用次数、调用账号数、收入（扣费流水合计）、上游成本与失败次数，另附最近的成功调用流水。
 * 设计意图：
 *   - 单价可以在表里直接改（标量价格的类型：图片每张、向量与转写与语音按百万单位），保存后立即对新调用生效，历史流水不改。
 *     视频、音乐与分时阶梯等复合价目无法用一个数表达，不提供行内修改，去「已发布模型」编辑。
 *   - 日期按自然日双侧包含（与后端一致），留空为全部历史；「今日调用」始终按当天统计，不受日期筛选影响。
 * 关键约束：只有 admin 能改价；收入、成本都是 1e-8 美元整数，用 useUsd 格式化；成本未知的请求不并入上游成本合计，列旁标出数量。
 */
import { IconCheck, IconPencil, IconX } from '@tabler/icons-react'
import { useState } from 'react'
import { catalogApi, type Capability } from '@ap/api-client/admin-catalog'
import { A } from '@ap/i18n/admin'
import { AG_catalog as G, errorTextCat } from '@ap/i18n/admin-catalog'
import { C } from '@ap/i18n/common'
import { useI18n } from '@ap/i18n'
import { fmtDateTime, useLoad, useToast } from '@ap/ui'
import { OpsNote, OpsPage, Pager, Stat, Stats, Toolbar } from '../../alice/kit.tsx'
import { dayOffset, fmtMs, pct } from '../../alice/kit-catalog.tsx'
import { useAdmin } from '../../session.tsx'
import { SYMBOL, num } from '../catalog/shared.ts'
import { useUsd } from '../../shared.tsx'

const LIMIT = 20

export function Capabilities() {
  const { t, locale } = useI18n()
  const toast = useToast()
  const usd = useUsd()
  const { isAdmin } = useAdmin()
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [offset, setOffset] = useState(0)
  const data = useLoad(() => catalogApi.capabilities({ start, end, limit: LIMIT, offset }), [start, end, offset])
  const [editing, setEditing] = useState('')
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)

  const d = data.data
  const sum = d?.summary
  const range = (days: number) => {
    setStart(dayOffset(days))
    setEnd(dayOffset(0))
    setOffset(0)
  }

  const save = async (c: Capability) => {
    setBusy(true)
    try {
      const r = await catalogApi.putCapabilityPrice(c.model_id, draft.trim())
      toast.ok(t(G.capabilities.priceSaved, { v: r.config_version }))
      setEditing('')
      data.reload()
    } catch (e) {
      console.error('[capabilities] 保存单价失败', e)
      toast.error(errorTextCat(e, locale))
    } finally {
      setBusy(false)
    }
  }

  const priceText = (c: Capability) => {
    if (c.price == null || c.price_unit == null) return null
    return `${SYMBOL[c.price_currency]}${num(c.price)}`
  }

  return (
    <OpsPage eyebrow="CAPABILITY USAGE" title={t(A.nav.capabilities)} desc={t(G.capabilities.desc)} chip={t(G.capabilities.chip)}>
      <Stats>
        <Stat label={t(G.capabilities.sRequests)} value={(sum?.requests ?? 0).toLocaleString()} sub={t(G.capabilities.sRequestsSub, { n: sum?.today_requests ?? 0 })} />
        <Stat label={t(G.capabilities.sOrgs)} value={(sum?.accounts ?? 0).toLocaleString()} sub={sum && sum.accounts ? t(G.capabilities.sOrgsSub, { n: (sum.requests / sum.accounts).toFixed(1) }) : '—'} />
        <Stat label={t(G.capabilities.sRevenue)} value={usd(sum?.cost ?? 0, 4)} color="success" sub={sum && sum.requests ? t(G.capabilities.sRevenueSub, { v: usd(sum.cost / sum.requests, 4) }) : '—'} />
        <Stat label={t(G.capabilities.sFailures)} value={(sum?.failures ?? 0).toLocaleString()} sub={t(G.capabilities.sFailuresSub)} />
      </Stats>

      <Toolbar count={d ? t(G.capabilities.total, { n: d.total }) : undefined}>
        <input
          type="date"
          className="cat-select"
          value={start}
          onChange={(e) => {
            setStart(e.target.value)
            setOffset(0)
          }}
        />
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t(G.capabilities.to)}</span>
        <input
          type="date"
          className="cat-select"
          value={end}
          onChange={(e) => {
            setEnd(e.target.value)
            setOffset(0)
          }}
        />
        <button className="btn btn-secondary btn-sm" onClick={() => range(0)}>
          {t(G.capabilities.today)}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => range(6)}>
          {t(G.capabilities.d7)}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => range(29)}>
          {t(G.capabilities.d30)}
        </button>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => {
            setStart('')
            setEnd('')
            setOffset(0)
          }}
        >
          {t(G.capabilities.clear)}
        </button>
      </Toolbar>
      <OpsNote>{t(G.capabilities.note)}</OpsNote>
      {data.error ? <div className="cat-banner error">{t(G.common.loadFailed, { msg: errorTextCat(data.error, locale) })}</div> : null}

      <div className="table-wrap" style={{ marginBottom: 20 }}>
        <div className="table-inner">
          <table>
            <thead>
              <tr>
                <th>{t(G.capabilities.colCap)}</th>
                <th>{t(A.models.kind)}</th>
                <th>{t(G.capabilities.colPrice)}</th>
                <th>{t(G.capabilities.colRequests)}</th>
                <th>{t(G.capabilities.colOrgs)}</th>
                <th>{t(G.capabilities.colRevenue)}</th>
                <th>{t(G.capabilities.colCost)}</th>
                <th>{t(G.capabilities.colFailures)}</th>
                <th>{t(G.capabilities.colShare)}</th>
                <th>{t(G.capabilities.colLast)}</th>
              </tr>
            </thead>
            <tbody>
              {(d?.capabilities ?? []).length === 0 ? (
                <tr>
                  <td colSpan={10} className="empty" style={{ textAlign: 'center', padding: 32, color: 'var(--text-tertiary)' }}>
                    {data.loading ? t(G.common.loading) : t(G.capabilities.empty)}
                  </td>
                </tr>
              ) : (
                (d?.capabilities ?? []).map((c) => {
                  const isEditing = editing === c.model_id
                  const pt = priceText(c)
                  return (
                    <tr key={c.model_id} style={c.enabled ? undefined : { opacity: 0.6 }}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{c.label || c.model_id}</div>
                        <div className="mono" style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                          {c.model_id}
                          {!c.enabled ? ` · ${t(A.status.disabled)}` : ''}
                        </div>
                      </td>
                      <td>
                        <span className="badge badge-info">{c.kind}</span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {isEditing ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <span>{SYMBOL[c.price_currency]}</span>
                            <input
                              className="form-input mono"
                              style={{ width: 90, padding: '4px 8px', fontSize: 12 }}
                              inputMode="decimal"
                              autoFocus
                              value={draft}
                              onChange={(e) => setDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.nativeEvent.isComposing) save(c)
                                if (e.key === 'Escape') setEditing('')
                              }}
                            />
                            <button className="btn btn-primary btn-sm" disabled={busy || !draft.trim()} onClick={() => save(c)} title={t(C.save)}>
                              <IconCheck size={12} />
                            </button>
                            <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setEditing('')} title={t(C.cancel)}>
                              <IconX size={12} />
                            </button>
                          </span>
                        ) : pt ? (
                          <span>
                            <span className="mono">{pt}</span>
                            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}> {c.price_unit ? t(G.capabilities.unit[c.price_unit]) : ''}</span>
                            {isAdmin && c.price_editable && (
                              <button
                                className="btn btn-secondary btn-sm"
                                style={{ marginLeft: 8 }}
                                title={t(G.capabilities.editPrice)}
                                onClick={() => {
                                  setEditing(c.model_id)
                                  setDraft(num(c.price))
                                }}
                              >
                                <IconPencil size={12} />
                              </button>
                            )}
                          </span>
                        ) : (
                          <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }} title={t(G.capabilities.complexHint)}>
                            {t(G.capabilities.complex)}
                          </span>
                        )}
                      </td>
                      <td>{c.requests.toLocaleString()}</td>
                      <td>{c.accounts.toLocaleString()}</td>
                      <td style={{ color: 'var(--success-text)' }}>{usd(c.cost, 4)}</td>
                      <td>
                        {usd(c.upstream_cost, 4)}
                        {c.unknown_cost_requests > 0 && (
                          <div style={{ fontSize: 11, color: 'var(--error-text)' }}>{t(G.capabilities.unknownCost, { n: c.unknown_cost_requests })}</div>
                        )}
                      </td>
                      <td style={c.failures ? { color: 'var(--error-text)' } : undefined}>{c.failures.toLocaleString()}</td>
                      <td>{pct(c.requests, sum?.requests ?? 0)}</td>
                      <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{c.last_used_at ? fmtDateTime(c.last_used_at, locale) : '—'}</td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ops-section-card">
        <div className="ops-section-head">
          <div>
            <div className="ops-section-title">{t(G.capabilities.recentTitle)}</div>
            <div className="ops-section-copy">{t(G.capabilities.recentCopy)}</div>
          </div>
        </div>
        <div className="table-wrap">
          <div className="table-inner">
            <table>
              <thead>
                <tr>
                  <th>{t(G.capabilities.rTime)}</th>
                  <th>{t(G.capabilities.rOrg)}</th>
                  <th>{t(G.capabilities.colCap)}</th>
                  <th>{t(G.capabilities.rChannel)}</th>
                  <th>{t(G.capabilities.rDuration)}</th>
                  <th>{t(G.capabilities.rCost)}</th>
                  <th>{t(G.capabilities.colCost)}</th>
                </tr>
              </thead>
              <tbody>
                {(d?.recent ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={7} className="empty" style={{ textAlign: 'center', padding: 28, color: 'var(--text-tertiary)' }}>
                      {data.loading ? t(G.common.loading) : t(G.capabilities.recentEmpty)}
                    </td>
                  </tr>
                ) : (
                  (d?.recent ?? []).map((r) => (
                    <tr key={r.request_id}>
                      <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDateTime(r.created_at, locale)}</td>
                      <td className="mono" style={{ fontSize: 11 }} title={r.account_id}>
                        {r.account_id.slice(0, 8)}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{r.label || r.model_id}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{r.kind}</div>
                      </td>
                      <td className="mono" style={{ fontSize: 11 }}>
                        {r.channel_id}/{r.upstream_model}
                      </td>
                      <td>{fmtMs(r.duration_ms)}</td>
                      <td>{usd(r.cost, 4)}</td>
                      <td>{r.upstream_cost == null ? <span style={{ color: 'var(--text-tertiary)' }}>{t(G.capabilities.costUnknown)}</span> : usd(r.upstream_cost, 4)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {d && d.total > 0 && (
            <div style={{ padding: '12px 16px', display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end' }}>
              <Pager offset={offset} limit={LIMIT} total={d.total} onChange={setOffset} />
            </div>
          )}
        </div>
      </div>
    </OpsPage>
  )
}
